import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerRequests, getSlackChannelsDropdown } from '../../services/CustomerRequestsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from '../../utils/Pagination';
import Footer from '../../Footer';

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const STATUS_COLORS = {
  Pending: { bg: '#ffc107', text: '#000' },
  Notified: { bg: '#0d6efd', text: '#fff' },
  CustomerResponded: { bg: '#198754', text: '#fff' },
  Resolved: { bg: '#6c757d', text: '#fff' },
  Escalated: { bg: '#dc3545', text: '#fff' },
};


function CustomerRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [trackingNumberFilter, setTrackingNumberFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  // Channels state
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);

  // Debounce the tracking number search
  const debouncedTrackingNumber = useDebounce(trackingNumberFilter, 500);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCustomerRequests({
        page: currentPage,
        pageSize,
        trackingNumber: debouncedTrackingNumber,
        status: statusFilter,
        channelId: channelFilter,
      });
      setRequests(data.items || []);
      setTotalItems(data.totalCount || 0);
    } catch (err) {
      setError('Failed to load customer requests');
      toast.error('Failed to load customer requests');
      setRequests([]);
      setTotalItems(0);
    }
    setLoading(false);
  }, [currentPage, pageSize, debouncedTrackingNumber, statusFilter, channelFilter]);

  // Fetch channels on mount
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const data = await getSlackChannelsDropdown();
        setChannels(data);
      } catch (err) {
        toast.error('Failed to load channels');
      }
      setChannelsLoading(false);
    };
    fetchChannels();
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleTrackingNumberChange = (e) => {
    setTrackingNumberFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleChannelChange = (e) => {
    setChannelFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleRowClick = (id) => {
    navigate(`/customer-requests/${id}`);
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const getStatusBadge = (status) => {
    const colors = STATUS_COLORS[status] || { bg: '#6c757d', text: '#fff' };
    return (
      <span
        className="badge"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '0.4em 0.8em',
          borderRadius: '0.25rem',
        }}
      >
        {status}
      </span>
    );
  };

  const getNotificationIcons = (notifications) => {
    const sms = notifications?.sms;
    const email = notifications?.email;

    if (!sms && !email) {
      return <span className="text-muted">-</span>;
    }

    return (
      <div className="d-flex gap-2">
        {/* SMS Icon */}
        <span
          title={sms ? (sms.isSuccessful ? `SMS sent to ${sms.recipient}` : `SMS failed to ${sms.recipient}`) : 'No SMS sent'}
        >
          <i
            className={`bi bi-chat-dots-fill ${sms ? (sms.isSuccessful ? 'text-success' : 'text-danger') : 'text-muted opacity-25'}`}
          ></i>
        </span>
        {/* Email Icon */}
        <span
          title={email ? (email.isSuccessful ? `Email sent to ${email.recipient}` : `Email failed to ${email.recipient}`) : 'No email sent'}
        >
          <i
            className={`bi bi-envelope-fill ${email ? (email.isSuccessful ? 'text-success' : 'text-danger') : 'text-muted opacity-25'}`}
          ></i>
        </span>
      </div>
    );
  };

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
      <ToastContainer />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Customer Support Requests</h4>
        <div className="d-flex align-items-center gap-2">
          <select
            className="form-select form-select-sm"
            value={channelFilter}
            onChange={handleChannelChange}
            disabled={loading || channelsLoading}
            style={{ width: 'auto', minWidth: '250px' }}
          >
            <option value="">All Channels</option>
            {channels.map((channel) => (
              <option key={channel.channelId} value={channel.channelId}>
                {channel.channelName}
              </option>
            ))}
          </select>
          <button
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={fetchRequests}
            disabled={loading}
            title="Refresh"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-arrow-clockwise me-2"></i>
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label fw-bold small text-muted">Tracking Number</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by tracking number..."
                  value={trackingNumberFilter}
                  onChange={handleTrackingNumberChange}
                  disabled={loading}
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold small text-muted">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={handleStatusChange}
                disabled={loading}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Notified">Notified</option>
                <option value="CustomerResponded">Customer Responded</option>
                <option value="Resolved">Resolved</option>
                <option value="Escalated">Escalated</option>
              </select>
            </div>
            <div className="col-md-2">
              {(trackingNumberFilter || statusFilter || channelFilter) && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setTrackingNumberFilter('');
                    setStatusFilter('');
                    setChannelFilter('');
                    setCurrentPage(1);
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading && requests.length === 0 ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Tracking Number</th>
                  <th>Slack Channel</th>
                  <th>Customer Name</th>
                  <th>Request Type</th>
                  <th>Status</th>
                  <th>Notifications</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No customer requests found
                    </td>
                  </tr>
                ) : (
                  requests.map((slackEvent) => (
                    <tr
                      key={slackEvent.id}
                      onClick={() => handleRowClick(slackEvent.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span className="fw-semibold">
                          {slackEvent.customerRequest?.trackingNumber || '-'}
                        </span>
                      </td>
                      <td>{slackEvent.channelName || '-'}</td>
                      <td>{slackEvent.customerRequest?.customerName || '-'}</td>
                      <td>{slackEvent.customerRequest?.requestType || '-'}</td>
                      <td>
                        {slackEvent.customerRequest?.status
                          ? getStatusBadge(slackEvent.customerRequest.status)
                          : <span className="text-muted">-</span>}
                      </td>
                      <td>{getNotificationIcons(slackEvent.customerRequest?.notifications)}</td>
                      <td>{new Date(slackEvent.createdAt).toLocaleString()}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(slackEvent.id);
                          }}
                          title="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="d-flex align-items-center">
              <select
                className="form-select form-select-sm me-2"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ width: 'auto' }}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <span className="text-muted">
                {totalItems > 0
                  ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, totalItems)} of ${totalItems} entries`
                  : 'No entries'}
              </span>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>

        </>
      )}

      <Footer />
    </div>
  );
}

export default CustomerRequests;
